import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'

import { logout } from 'util/api'
import { getCache } from 'util/cache'

import 'css/components/navbar.css'
// The rebranded wordmark, taken from the customer's own mockups (the same
// asset appears in 10 of the 11 screens). The three pre-refresh logo files
// that used to live in src/img were deleted with this change -- they all
// predated the 8/21 rebrand and were the easiest way to reintroduce the old
// mark by reaching for an obvious filename.
import logo from 'img/logo-main.png'

export const Navbar = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const loggedIn = localStorage.getItem('loggedIn')

	const menuButton = useRef(null)
	const navItems = useRef(null)

	const [expand, setExpand] = useState(false)
	const [profilePic, setProfilePic] = useState(getCache('profilePicture'))
	const [name, setName] = useState(getCache('firstName'))
	const [email, setEmail] = useState(getCache('email'))

	useEffect(() => {
		const handleProfilePicUpdated = () => {
			setProfilePic(getCache('profilePicture'))
		}
		window.addEventListener('afo:profilePicUpdated', handleProfilePicUpdated)
		return () => window.removeEventListener('afo:profilePicUpdated', handleProfilePicUpdated)
	}, [])

	// getUserDetails() only runs on specific pages (dashboard, settings), so
	// the who-block picks up cached name/email whenever navigation happens
	// rather than only at mount.
	useEffect(() => {
		setName(getCache('firstName'))
		setEmail(getCache('email'))
	}, [location.pathname])

	const handleLogout = async () => {
		setExpand(false)
		await logout()
		localStorage.removeItem('loggedIn')
		localStorage.removeItem('profilePicture')
		localStorage.removeItem('firstName')
		navigate('/login')
	}


	useEffect(() => {
		function handleClickOutside(event) {
			const buttonEl = menuButton.current
			const itemsEl = navItems.current

			if (!buttonEl && !itemsEl) return

			const clickOnButton = buttonEl && buttonEl.contains(event.target)
			const clickOnItems = itemsEl && itemsEl.contains(event.target)

			if (clickOnButton || clickOnItems) return

			setExpand(false)
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const navLinks = [
		{ to: '/classrooms', label: 'My classrooms' },
		{ to: '/upload-lessons', label: 'Plan a new lesson' },
		{ to: '/lessons', label: 'Library' },
	]

	return (
		<nav className={"navbar " + (loggedIn ? "logged-in" : "")}>
			<Link to="/dashboard">
				<img className="logo" src={logo} />
			</Link>
			{/* The mockup's <header> is space-between over THREE children --
			    brand / nav / account -- which is what centres the link group in
			    the gap (measured: links start at x=610 on a 1440 viewport).
			    Grouping the links together with the account button made the
			    header a two-child flex row and pushed them out to x=930. */}
			{loggedIn && (
				<nav className="nav-links" aria-label="Main">
					{navLinks.map(({ to, label }) => (
						<Link
							key={to}
							to={to}
							className={location.pathname.startsWith(to) ? 'active' : ''}
							onClick={() => setExpand(false)}
						>
							{label}
						</Link>
					))}
				</nav>
			)}
			{/* The mockups' auth screen (AllForOne_Auth.html) has no account
			    control at all and centres the brand -- staging was showing a
			    signed-out visitor an account avatar. Both auth pages already
			    cross-link to each other, so nothing is stranded by dropping it. */}
			{loggedIn && (
			<div className="nav-right">
				<div className="account">
					<button
						className="menu-btn"
						ref={menuButton}
						aria-haspopup="true"
						aria-expanded={expand}
						aria-label="Account menu"
						onClick={() => setExpand(!expand)}
					>
						{profilePic ? (
							<img className="profile-pic" src={profilePic} alt="" />
						) : (
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
								<circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
							</svg>
						)}
					</button>
					<div className={"dropdown " + (expand ? "open" : "")} ref={navItems}>
								{(name || email) && (
									<div className="who">
										{name && <strong>{name}</strong>}
										{email && <span>{email}</span>}
									</div>
								)}
								<Link className="menu-item" to="/dashboard" onClick={() => setExpand(false)}>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" />
									</svg>
									Teacher dashboard
								</Link>
								<Link className="menu-item" to="/settings" onClick={() => setExpand(false)}>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.14.36.44.63.81.76H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
									</svg>
									Settings
								</Link>
								<hr className="menu-sep" />
								<button className="menu-item danger" onClick={handleLogout}>
									<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
									</svg>
									Sign out
								</button>
					</div>
				</div>
			</div>
			)}
		</nav>
	)
}
